import {
    currentOrganization,
    needsOrgSelection,
    Organization,
    orgSetFromLogin,
} from '../state/organization';

/**
 * Service to sync Pillar user data independently from authentication
 * This service is called after the user is authenticated to fetch and store
 * the pillarId and role in the session
 */

export interface SyncPillarResponse {
    success: boolean;
    pillarId?: string;
    role?: string;
    organization?: Organization;
    error?: string;
    details?: string;
}

/**
 * Syncs Pillar user data with the current session
 * Should be called after user authentication is complete
 *
 * @returns Promise that resolves with pillar data when sync is complete
 */
export const syncPillarData = async (): Promise<SyncPillarResponse | null> => {
    try {
        const response = await fetch('/api/sync-pillar', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        });

        if (!response.ok) {
            const errorData: SyncPillarResponse = await response.json();
            console.warn(
                '[PILLAR-SYNC] Failed to sync Pillar data:',
                errorData.error,
                errorData.details
            );
            // Don't throw error - pillar sync is optional and shouldn't block app
            return null;
        }

        const data: SyncPillarResponse = await response.json();

        // If organization was returned (from WorkOS org selection at login),
        // update the organization state - no need for manual selection
        if (data.organization) {
            currentOrganization.value = data.organization;
            needsOrgSelection.value = false;
            orgSetFromLogin.value = true; // Hide the organization switcher
            console.log(
                '[PILLAR-SYNC] Organization set from login:',
                data.organization.name,
            );
        }

        return data;
    } catch (error) {
        // Log error but don't throw - pillar sync is non-blocking
        console.warn('[PILLAR-SYNC] Error syncing Pillar data:', error);
        return null;
    }
};
